import React, { useState } from 'react';
import { ArrowRight, Battery, CheckCircle, Clock, Users, Award, Mail, Bell, ChevronDown, Menu, X, Calendar, DollarSign, Zap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

const LithiumTeaser = () => {
  const { language } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNotifyMe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const courseFeatures = [
    {
      icon: Battery,
      title: "Battery Pack Construction",
      description: "Learn professional techniques for building custom lithium-ion battery packs from recycled 18650 cells.",
      highlight: "Hands-on Building"
    },
    {
      icon: Zap,
      title: "Recycling & Sustainability",
      description: "Master advanced recycling techniques and sustainable energy solutions to reduce environmental impact.",
      highlight: "Eco-Friendly"
    },
    {
      icon: DollarSign,
      title: "Cost Optimization",
      description: "Discover how to save up to 70% on battery costs through smart sourcing and recycling practices.",
      highlight: "70% Savings"
    },
    {
      icon: Award,
      title: "Professional Certification",
      description: "Receive industry-recognized certification upon successful completion of the course.",
      highlight: "Certified"
    }
  ];

  const curriculum = [
    {
      week: 1,
      title: "Battery Fundamentals",
      topics: ["Lithium-ion Chemistry", "Cell Types & Specifications", "Safety Protocols"]
    },
    {
      week: 2,
      title: "Cell Testing & Selection",
      topics: ["Capacity Testing", "Internal Resistance", "Cell Matching"]
    },
    {
      week: 3,
      title: "Pack Design & Assembly",
      topics: ["Series vs Parallel", "BMS Integration", "Mechanical Assembly"]
    },
    {
      week: 4,
      title: "Recycling Techniques",
      topics: ["Cell Recovery", "Refurbishment Process", "Quality Assessment"]
    },
    {
      week: 5,
      title: "Advanced Applications",
      topics: ["Custom Configurations", "Performance Optimization", "Troubleshooting"]
    },
    {
      week: 6,
      title: "Final Project",
      topics: ["Complete Pack Build", "Testing & Validation", "Certification Assessment"]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/qubit-network-logo.svg" 
                alt="Qubit Center" 
                className="w-8 h-8"
                style={{ filter: 'brightness(0) saturate(100%)' }}
              />
              <span className="text-xl font-semibold text-gray-900 dark:text-white">
                Qubit Center
              </span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {language === 'ar' ? 'الرئيسية' : 'Home'}
              </a>
              <a href="/cpp-course" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {language === 'ar' ? 'دورة C++' : 'C++ Course'}
              </a>
            </nav>
            
            <div className="flex items-center gap-4">
              <LanguageToggle variant="button" />
              
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium mb-8">
              <Clock className="w-4 h-4" />
              {language === 'ar' ? 'قريباً - 2025' : 'Coming Soon - 2025'}
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              {language === 'ar' ? (
                <>دورة بطاريات<br />الليثيوم المتقدمة</>
              ) : (
                <>Advanced Lithium-ion<br />Battery Course</>
              )}
            </h1>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
              {language === 'ar' 
                ? 'تعلم بناء حزم البطاريات المهنية مع تقنيات إعادة التدوير المتقدمة. وفر حتى 70% من التكاليف مع الحفاظ على البيئة.'
                : 'Master professional battery pack building with advanced recycling techniques. Save up to 70% on costs while protecting the environment.'
              }
            </p>

            {/* Early Access Form */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 max-w-md mx-auto">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'كن أول من يعرف' : 'Be the First to Know'}
              </h3>
              
              {isSubscribed ? (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>{language === 'ar' ? 'شكراً! سنتواصل معك قريباً' : 'Thank you! We\'ll notify you soon'}</span>
                </div>
              ) : (
                <form onSubmit={handleNotifyMe} className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                  <button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    <Bell className="w-4 h-4" />
                    {language === 'ar' ? 'أبلغني عند الإعلان' : 'Notify Me When Available'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800"></div>
        <div className="absolute inset-0 -z-10 bg-grid-pattern opacity-5"></div>
      </section>

      {/* Course Features */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {language === 'ar' ? 'ماذا ستتعلم؟' : 'What You\'ll Learn'}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {language === 'ar' ? 'تجربة تعليمية شاملة في تقنيات البطاريات' : 'Comprehensive learning experience in battery technology'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {courseFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white dark:bg-gray-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    {language === 'ar' ? (
                      feature.title === 'Battery Pack Construction' ? 'بناء حزم البطاريات' :
                      feature.title === 'Recycling & Sustainability' ? 'إعادة التدوير والاستدامة' :
                      feature.title === 'Cost Optimization' ? 'تحسين التكاليف' : 'شهادة مهنية'
                    ) : feature.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                    {language === 'ar' ? (
                      feature.description === 'Learn professional techniques for building custom lithium-ion battery packs from recycled 18650 cells.' 
                        ? 'تعلم التقنيات المهنية لبناء حزم البطاريات المخصصة من خلايا 18650 المعاد تدويرها.' 
                        : feature.description === 'Master advanced recycling techniques and sustainable energy solutions to reduce environmental impact.'
                        ? 'أتقن تقنيات إعادة التدوير المتقدمة وحلول الطاقة المستدامة لتقليل التأثير البيئي.'
                        : feature.description === 'Discover how to save up to 70% on battery costs through smart sourcing and recycling practices.'
                        ? 'اكتشف كيفية توفير حتى 70% من تكاليف البطاريات من خلال المصادر الذكية وممارسات إعادة التدوير.'
                        : 'احصل على شهادة معترف بها في المجال عند إتمام الدورة بنجاح.'
                    ) : feature.description}
                  </p>
                  
                  <div className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold">
                    <CheckCircle className="w-4 h-4" />
                    {feature.highlight}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Curriculum Preview */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {language === 'ar' ? 'المنهج الدراسي' : 'Course Curriculum'}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {language === 'ar' ? 'برنامج تدريبي مكثف لمدة 6 أسابيع' : '6-week intensive training program'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {curriculum.map((week, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-blue-50 dark:bg-blue-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-300">
                    {language === 'ar' ? `الأسبوع ${week.week}` : `Week ${week.week}`}
                  </h3>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                    {language === 'ar' ? (
                      week.title === 'Battery Fundamentals' ? 'أساسيات البطاريات' :
                      week.title === 'Cell Testing & Selection' ? 'اختبار واختيار الخلايا' :
                      week.title === 'Pack Design & Assembly' ? 'تصميم وتجميع الحزم' :
                      week.title === 'Recycling Techniques' ? 'تقنيات إعادة التدوير' :
                      week.title === 'Advanced Applications' ? 'التطبيقات المتقدمة' : 'المشروع النهائي'
                    ) : week.title}
                  </h4>
                </div>
                
                <div className="p-6">
                  <ul className="space-y-2">
                    {week.topics.map((topic, topicIndex) => (
                      <li key={topicIndex} className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Stats */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">6</div>
              <div className="text-gray-600 dark:text-gray-400">
                {language === 'ar' ? 'أسابيع' : 'Weeks'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">70%</div>
              <div className="text-gray-600 dark:text-gray-400">
                {language === 'ar' ? 'توفير في التكاليف' : 'Cost Savings'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">24/7</div>
              <div className="text-gray-600 dark:text-gray-400">
                {language === 'ar' ? 'الدعم' : 'Support'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">100%</div>
              <div className="text-gray-600 dark:text-gray-400">
                {language === 'ar' ? 'عملي' : 'Hands-on'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            {language === 'ar' ? 'ابق متابعاً للإعلان' : 'Stay Tuned for the Launch'}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            {language === 'ar' 
              ? 'سنعلن عن مواعيد التسجيل والتفاصيل الكاملة قريباً'
              : 'We\'ll announce registration dates and full details very soon'
            }
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/"
              className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              <ArrowRight className="w-5 h-5" />
            </a>
            
            <a
              href="/cpp-course"
              className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
            >
              {language === 'ar' ? 'تصفح دورة C++' : 'Browse C++ Course'}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="/qubit-network-logo.svg" 
                alt="Qubit Center" 
                className="w-8 h-8"
                style={{ filter: 'brightness(0) saturate(100%) invert(0.5)' }}
              />
              <span className="text-xl font-semibold text-gray-900 dark:text-white">Qubit Center</span>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {language === 'ar' 
                ? 'منصة تعليمية متطورة لتعلم التقنيات الحديثة'
                : 'Advanced learning platform for modern technology education'
              }
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              © 2025 Qubit Center. {language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LithiumTeaser;